/**
 * Campaign attribution depends on the query string the visitor ARRIVED with
 * (mPubGroupID, sub1, sub2, utm_*, gclid, ...). Those params only exist on the
 * landing URL, so anything read from `window.location` later in the funnel —
 * a lead post from /matches, a webhook from /thank-you — sees an empty query
 * and silently mis-attributes the lead.
 *
 * The arrival query string is therefore captured once per session and reused.
 */
const LANDING_SEARCH_KEY = 'mm_landing_search'

/**
 * The query string the visitor arrived with, as a leading-`?` string (or '').
 *
 * Captured on first call and then held for the session. A later URL that
 * carries params is merged in, so a visitor who lands on a plain page and then
 * follows a tagged link is still attributed.
 */
export function getLandingSearch(): string {
  if (typeof window === 'undefined') return ''
  const current = window.location.search
  try {
    const stored = sessionStorage.getItem(LANDING_SEARCH_KEY)
    if (!stored) {
      if (current) sessionStorage.setItem(LANDING_SEARCH_KEY, current)
      return current
    }
    if (!current) return stored
    // Params present on the current URL win over the stored copy.
    const merged = new URLSearchParams(stored)
    new URLSearchParams(current).forEach((value, key) => merged.set(key, value))
    const next = merged.toString() ? `?${merged.toString()}` : ''
    sessionStorage.setItem(LANDING_SEARCH_KEY, next)
    return next
  } catch {
    return current
  }
}

/** Records params seen on the current URL without reading anything back. */
export function captureLandingSearch(): void {
  getLandingSearch()
}

/**
 * Merges the session's params into `search`, with `search` taking precedence.
 * Returns a leading-`?` string, or '' when there is nothing to carry.
 */
export function mergeLandingSearch(search: string): string {
  const landing = getLandingSearch()
  if (!landing) return search
  const merged = new URLSearchParams(landing)
  new URLSearchParams(search).forEach((value, key) => merged.set(key, value))
  const query = merged.toString()
  return query ? `?${query}` : ''
}

/** Appends the session's params to an internal path for `navigate` / `Link`. */
export function withSearch(path: string): string {
  const [pathname, existing = ''] = path.split('?')
  const search = mergeLandingSearch(existing ? `?${existing}` : '')
  return `${pathname}${search}`
}
